import React, { useState, useRef } from 'react';
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
  Slider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { format } from 'date-fns';
import { CallResponse } from 'retell-sdk/resources/call';

import { isPhoneCall, isWebCall } from '../../api/hooks/calls/getCallList';
import { formatDuration } from '../../common/commonUtils';
import RetellCallTranscriptionSection from './RetellCallTranscriptionSection';

interface RetellCallDetailsComponentProps {
  call: CallResponse | null;
  open: boolean;
  onClose: () => void;
}

const RetellCallDetailsComponent: React.FC<RetellCallDetailsComponentProps> = ({
  call,
  open,
  onClose,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const volumeControlRef = useRef<HTMLDivElement>(null);

  const formatTimestamp = (timestamp: number): string => {
    return format(new Date(timestamp), 'MMM dd, yyyy HH:mm:ss');
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'registered':
        return 'success';
      case 'failed':
        return 'error';
      case 'in_progress':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getCallDuration = (): number => {
    if (call?.start_timestamp && call?.end_timestamp) {
      return Math.floor((call.end_timestamp - call.start_timestamp) / 1000);
    }
    return 0;
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const progress =
        (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setAudioProgress(progress);
      setCurrentTime(formatDuration(Math.floor(audioRef.current.currentTime)));
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(formatDuration(Math.floor(audioRef.current.duration)));
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setAudioProgress(0);
    setCurrentTime('0:00');
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current) {
      const progressBar = e.currentTarget;
      const clickPosition =
        e.clientX - progressBar.getBoundingClientRect().left;
      const progressBarWidth = progressBar.clientWidth;
      const seekPercentage = clickPosition / progressBarWidth;
      audioRef.current.currentTime = audioRef.current.duration * seekPercentage;
      setAudioProgress(seekPercentage * 100);
    }
  };

  const handleVolumeChange = (_event: Event, newValue: number | number[]) => {
    const newVolume = newValue as number;
    setVolume(newVolume);

    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      const newMuteState = !isMuted;
      setIsMuted(newMuteState);

      if (newMuteState) {
        audioRef.current.volume = 0;
      } else {
        audioRef.current.volume = volume > 0 ? volume : 0.5;
      }
    }
  };

  const resolveRecordingUrl = (c: CallResponse): string | undefined => {
    const record = c as unknown as Record<string, unknown>;
    if (typeof record.scrubbed_recording_url === 'string') {
      return record.scrubbed_recording_url;
    }
    return c.recording_url ?? undefined;
  };

  const recordingUrl = call ? resolveRecordingUrl(call) : undefined;

  if (!call) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: '100%',
              sm: '450px',
              md: '600px',
            },
          },
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5">Call Details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <div>
          <Box sx={{ mb: 4, bgcolor: '#f5f5f5', borderRadius: 2, p: 2 }}>
            {recordingUrl ? (
              <>
                <audio
                  ref={audioRef}
                  src={recordingUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleAudioEnded}
                >
                  <track kind="captions" src="" label="English captions" />
                </audio>

                {/* More compact audio player layout */}
                <Box
                  display="flex"
                  alignItems="center"
                  width="100%"
                  sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <IconButton onClick={handlePlayPause} size="small">
                      {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{ mr: 1, fontSize: '0.8rem', minWidth: '60px' }}
                  >
                    {currentTime} / {duration}
                  </Typography>

                  <Box
                    sx={{
                      height: '4px',
                      bgcolor: 'grey.300',
                      borderRadius: 1,
                      position: 'relative',
                      cursor: 'pointer',
                      flexGrow: 1,
                      mx: 0.5,
                    }}
                    onClick={handleSeek}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${audioProgress}%`,
                        bgcolor: 'primary.main',
                        borderRadius: 1,
                      }}
                    />
                  </Box>

                  {/* Modified Volume Control with inline slider */}
                  <Box
                    ref={volumeControlRef}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      ml: 1,
                      mr: 1,
                    }}
                    onMouseEnter={() => setShowVolumeSlider(true)}
                    onMouseLeave={() => setShowVolumeSlider(false)}
                  >
                    <IconButton
                      onClick={toggleMute}
                      size="small"
                      sx={{ p: 0.5 }}
                    >
                      {isMuted ? (
                        <VolumeOffIcon fontSize="small" />
                      ) : (
                        <VolumeUpIcon fontSize="small" />
                      )}
                    </IconButton>

                    <Box
                      sx={{
                        position: 'relative',
                        width: showVolumeSlider ? '70px' : '0px',
                        opacity: showVolumeSlider ? 1 : 0,
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <Slider
                        size="small"
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        min={0}
                        max={1}
                        step={0.01}
                        sx={{
                          '& .MuiSlider-thumb': {
                            width: 10,
                            height: 10,
                            color: 'gray',
                          },
                          '& .MuiSlider-rail, & .MuiSlider-track': {
                            height: 3,
                            color: 'gray',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </>
            ) : (
              <Typography color="text.secondary">
                No recording available
              </Typography>
            )}
          </Box>
        </div>

        {/* Basic Information */}
        <div>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>

            <Stack spacing={2}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Call Type
                </Typography>
                <Typography variant="body2">
                  {isWebCall(call) ? 'Web Call' : 'Phone Call'}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip
                  label={call.call_status || 'Unknown'}
                  color={getStatusColor(call.call_status || '')}
                  size="small"
                />
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Start Time
                </Typography>
                <Typography variant="body2">
                  {call.start_timestamp
                    ? formatTimestamp(call.start_timestamp)
                    : 'N/A'}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  End Time
                </Typography>
                <Typography variant="body2">
                  {call.end_timestamp
                    ? formatTimestamp(call.end_timestamp)
                    : 'N/A'}
                </Typography>
              </Box>

              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Duration
                </Typography>
                <Typography variant="body2">
                  {call.start_timestamp && call.end_timestamp
                    ? formatDuration(getCallDuration())
                    : 'N/A'}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </div>

        {/* Call ID & Phone Details */}
        <div>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Call ID & Phone Details
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Call ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    wordBreak: 'break-all',
                    mt: 0.5,
                  }}
                >
                  {call.call_id}
                </Typography>
              </Box>

              {isPhoneCall(call) && (
                <>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      From Number
                    </Typography>
                    <Typography variant="body2">
                      {call.from_number || 'N/A'}
                    </Typography>
                  </Box>

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      To Number
                    </Typography>
                    <Typography variant="body2">
                      {call.to_number || 'N/A'}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          </Paper>
        </div>

        {/* Transcription Section */}
        <div>
          <RetellCallTranscriptionSection call={call} open={open} />
        </div>
      </Box>
    </Drawer>
  );
};

export default RetellCallDetailsComponent;
